import { ItemList, ListItem } from 'schema-dts';

import { UDACITY_ORGANIZATION_NAME, UDACITY_ORGANIZATION_URL } from './organization-data';

export type CourseListItem = {
  slug?: string | null;
  title?: string | null;
  description?: string | null;
};

// Minimal inline provider matching Google's Course list rich result example
// (https://developers.google.com/search/docs/appearance/structured-data/course).
// Reuses the shared org name/url constants (single source of truth) but stays small
// and inlined per-item so each page's structured data is self-contained — Google
// evaluates pages independently, so an @id reference would orphan without a co-located
// Organization node.
const COURSE_PROVIDER = {
  '@type': 'Organization',
  name: UDACITY_ORGANIZATION_NAME,
  sameAs: UDACITY_ORGANIZATION_URL,
} as const;

// Builds an ItemList of nested Course entities for the Course Carousel rich result.
// Each ListItem nests the Course under `item` with url/name/description/provider, and
// positions are 1-based sequential (both required by Google). Items missing a slug or
// title are skipped so the carousel only contains linkable, labelled programs.
export function createCourseListStructuredData(items: CourseListItem[]): ItemList {
  const itemListElement: ListItem[] = items
    .filter((item): item is CourseListItem & { slug: string; title: string } => Boolean(item.slug && item.title))
    .map(({ slug, title, description }, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Course',
        url: `https://www.udacity.com/course/${slug}`,
        name: title,
        ...(description ? { description } : {}),
        provider: COURSE_PROVIDER,
      },
    }));

  return {
    '@type': 'ItemList',
    itemListElement,
  };
}
